import React, { useEffect, useRef, useState } from 'react';
import { log } from '../utils/logger';

const COMPONENT = 'Turnstile';

const SCRIPT_ID = 'turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** How long to wait for `window.turnstile` to appear before declaring failure. */
const API_READY_TIMEOUT_MS = 20_000;
/** How long after render() to wait for Cloudflare's iframe before declaring failure. */
const WIDGET_PAINT_TIMEOUT_MS = 15_000;
/**
 * Grace period before reclaiming the reserved box height. An INVISIBLE widget
 * paints nothing, so the space held open for a managed checkbox stays an empty
 * gap forever; a managed widget needs that space held from the first frame or
 * the form jumps when the checkbox appears. Waiting, then measuring, serves both.
 */
const INVISIBLE_COLLAPSE_DELAY_MS = 2_500;

/**
 * Single shared loader for Cloudflare's api.js.
 *
 * ── WHY A PROMISE AND NOT `script.onload` ───────────────────────────────────
 * Two independent races made the widget silently never appear in production:
 *
 * 1. `script.onload` is NOT a readiness signal. api.js is a loader; with
 *    `render=explicit` it can finish downloading before it has published
 *    `window.turnstile`. Code that renders on `onload` finds `window.turnstile`
 *    undefined, bails, and — because `onload` fires exactly once — is never
 *    woken again.
 * 2. `document.getElementById(SCRIPT_ID)` being non-null means "some other
 *    instance started the load", NOT "the API is ready". The login forms also
 *    mount a Turnstile, so a second instance would treat an in-flight script as
 *    loaded and dead-end in the same way.
 *
 * Both are fixed by resolving on the only fact that matters — `window.turnstile`
 * actually existing — polled until it does, shared across every instance.
 * ────────────────────────────────────────────────────────────────────────────
 */
let loaderPromise: Promise<void> | null = null;

function loadTurnstileApi(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('no-window'));
  }
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (loaderPromise) {
    return loaderPromise;
  }

  loaderPromise = new Promise<void>((resolve, reject) => {
    const startedAt = Date.now();
    let pollId: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (pollId !== null) {
        clearInterval(pollId);
        pollId = null;
      }
    };

    const fail = (reason: string) => {
      stopPolling();
      loaderPromise = null; // allow a later mount to retry
      reject(new Error(reason));
    };

    pollId = setInterval(() => {
      if (window.turnstile) {
        stopPolling();
        resolve();
        return;
      }
      if (Date.now() - startedAt > API_READY_TIMEOUT_MS) {
        fail('api-ready-timeout');
      }
    }, 50);

    // Another instance may already have injected the tag — the poll above is
    // what resolves us in that case, so there is nothing else to do.
    if (document.getElementById(SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => log.info(COMPONENT, 'Turnstile script loaded');
    script.onerror = () => {
      // Remove the tag so a retry is not mistaken for an in-flight load.
      script.remove();
      log.error(COMPONENT, 'Failed to load Turnstile script');
      fail('script-load-failed');
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
}

/**
 * Human-readable text for the Turnstile client-side error codes that mean
 * "misconfigured", not "this visitor failed the challenge". Cloudflare passes
 * the code as the first argument to `error-callback`; dropping it is what made
 * a bad site key look identical to a working widget that just hadn't painted.
 * See https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/
 */
function describeTurnstileError(code?: string): string {
  if (!code) return 'Verification failed. Please try again.';
  if (code.startsWith('110200')) {
    return `This site is not on the Turnstile widget's allowed-domains list (error ${code}). ` +
           'Add this hostname to the widget in the Cloudflare dashboard.';
  }
  if (code.startsWith('110100') || code.startsWith('110110')) {
    return `The Turnstile site key is not valid for this site (error ${code}). ` +
           'Check VITE_TURNSTILE_SITE_KEY.';
  }
  return `Verification failed (error ${code}). Please try again.`;
}

interface TurnstileProps {
  siteKey: string;
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  /**
   * The widget could not be shown AT ALL (no site key, api.js never loaded,
   * render threw, nothing painted). Distinct from onError, which also fires
   * when a visitor FAILS the challenge — a caller that offers a fallback
   * (HumanGate's "Continue anyway") must only do so for this one.
   */
  onUnavailable?: () => void;
  /**
   * Cloudflare decided this visitor must click (managed widget escalated to
   * an interactive checkbox) / the click is done. Lets a caller say "tick the
   * box" only when there IS a box — an invisible widget never calls these.
   */
  onInteractive?: () => void;
  onInteractiveDone?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  action?: string;
  cData?: string;
  className?: string;
  /**
   * Bump this number to force a fresh challenge. Turnstile tokens are
   * SINGLE-USE: once a token has been posted to the backend it is spent, so a
   * failed submit must reset the widget or the retry posts a dead token and
   * fails again. Parents increment this on every failed submit.
   */
  resetKey?: number;
}

/**
 * Cloudflare Turnstile CAPTCHA Component
 * Privacy-friendly, invisible CAPTCHA for bot protection
 *
 * FIXES for React/Vite SPA Issues:
 * 1. Hard-isolated container prevents invisible overlay bug
 * 2. Explicit rendering prevents DOM search issues
 * 3. Pointer events management ensures form fields are clickable
 * 4. Fixed height prevents layout shifts
 *
 * ── ALWAYS-MOUNTED CONTRACT ─────────────────────────────────────────────────
 * The registration forms mount this unconditionally, which makes three former
 * non-issues load-bearing:
 *
 * 1. Callbacks live in a ref and are NOT effect dependencies. Parents pass
 *    inline arrows that change identity on every render; if those were deps,
 *    every keystroke in the form would remove and re-render the widget.
 * 2. An error NEVER unmounts the container. It used to `return <div>{error}</div>`
 *    instead of the widget host, which permanently killed the widget: the render
 *    effect could not re-attach because containerRef.current was null. A token
 *    expiring (~5 min) would have bricked the form. Errors now render ALONGSIDE
 *    a container that stays mounted for the component's whole life.
 * 3. Expiry is recoverable. Turnstile's default refresh-expired=auto re-runs the
 *    challenge itself; we only clear the parent's stale token.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ── NEVER FAIL SILENTLY ─────────────────────────────────────────────────────
 * Registration cannot be submitted without a token, so a widget that renders
 * nothing is a hard outage that looks like a cosmetic gap ("only the label is
 * visible"). Every failure path here therefore ends in visible text: a missing
 * site key, an api.js that never becomes ready, a Cloudflare error code, and —
 * the catch-all — a `render()` that returned without ever painting an iframe.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const Turnstile: React.FC<TurnstileProps> = ({
  siteKey,
  onSuccess,
  onError,
  onExpire,
  onUnavailable,
  onInteractive,
  onInteractiveDone,
  theme = 'auto',
  size = 'normal',
  action,
  cData,
  className = '',
  resetKey = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [apiReady, setApiReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const trimmedKey = siteKey?.trim() ?? '';

  // Latest callbacks, so the render effect below never depends on their identity.
  const cbRef = useRef({ onSuccess, onError, onExpire, onUnavailable, onInteractive, onInteractiveDone });
  useEffect(() => {
    cbRef.current = { onSuccess, onError, onExpire, onUnavailable, onInteractive, onInteractiveDone };
  }, [onSuccess, onError, onExpire, onUnavailable, onInteractive, onInteractiveDone]);

  // Load api.js and wait for window.turnstile to actually exist.
  useEffect(() => {
    if (!trimmedKey) {
      log.error(COMPONENT, 'No Turnstile site key configured');
      setError('Human verification is not configured for this site (missing site key).');
      cbRef.current.onUnavailable?.();
      return;
    }

    let cancelled = false;
    loadTurnstileApi().then(
      () => {
        if (!cancelled) setApiReady(true);
      },
      (err: Error) => {
        if (cancelled) return;
        log.error(COMPONENT, 'Turnstile API never became available', { err: err.message });
        setError(
          err.message === 'script-load-failed'
            ? 'Could not load the human-verification widget. Check your connection and reload.'
            : 'The human-verification widget did not start. Please reload the page.'
        );
        cbRef.current.onUnavailable?.();
      }
    );
    return () => {
      cancelled = true;
    };
  }, [trimmedKey]);

  // Render the widget explicitly once the API is genuinely ready.
  useEffect(() => {
    if (!apiReady || !trimmedKey || !containerRef.current || !window.turnstile) {
      return;
    }
    const container = containerRef.current;
    const turnstile = window.turnstile;

    // Remove existing widget if any
    if (widgetIdRef.current) {
      try {
        turnstile.remove(widgetIdRef.current);
      } catch (e) {
        log.warn(COMPONENT, 'Error removing previous Turnstile widget', { err: String(e) });
      }
      widgetIdRef.current = null;
    }

    // Clear container to ensure clean slate
    container.innerHTML = '';

    // Catch-all: render() can return without ever attaching anything (bad site
    // key, blocked network) and without invoking error-callback. Say so instead
    // of leaving an empty box.
    //
    // "Attached" = the container got ANY child, not "an iframe is findable".
    // Cloudflare's markup is not ours to query: an INVISIBLE widget (the
    // production key) paints nothing visible, and the iframe can sit where
    // querySelector does not reach. Looking for an iframe declared a working,
    // still-running challenge dead at 15 s — on the home gate that flashed
    // "Try again / Continue anyway" a second before the token arrived
    // (Production-Issues/Oct4-2026-ScoutBot/CLAUDE-DONE-human-gate-ux.md).
    let paintWatchdog: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      paintWatchdog = null;
      if (container.childElementCount === 0) {
        log.error(COMPONENT, 'Turnstile widget never painted', { siteKey: trimmedKey });
        setError(
          'The human-verification widget could not be displayed. ' +
          'Please reload; if it persists the site key or its allowed domains need attention.'
        );
        cbRef.current.onUnavailable?.();
      }
    }, WIDGET_PAINT_TIMEOUT_MS);

    const clearWatchdog = () => {
      if (paintWatchdog !== null) {
        clearTimeout(paintWatchdog);
        paintWatchdog = null;
      }
    };

    // Render new widget with explicit rendering
    try {
      widgetIdRef.current = turnstile.render(container, {
        sitekey: trimmedKey,
        theme,
        size,
        action,
        cData,
        callback: (token: string) => {
          clearWatchdog();
          setError(null);
          cbRef.current.onSuccess(token);
        },
        'error-callback': (code?: string) => {
          clearWatchdog();
          log.error(COMPONENT, 'Turnstile challenge failed', { code: code ?? 'unknown' });
          setError(describeTurnstileError(code));
          cbRef.current.onError?.();
        },
        'before-interactive-callback': () => cbRef.current.onInteractive?.(),
        'after-interactive-callback': () => cbRef.current.onInteractiveDone?.(),
        'unsupported-callback': () => {
          clearWatchdog();
          log.error(COMPONENT, 'Turnstile reports this browser is unsupported');
          setError('This browser cannot run the human-verification check.');
          cbRef.current.onUnavailable?.();
        },
        'expired-callback': () => {
          // Not an error state: refresh-expired defaults to "auto", so the
          // widget re-challenges itself. Just drop the parent's stale token.
          log.warn(COMPONENT, 'Turnstile token expired — re-challenging');
          cbRef.current.onExpire?.();
        },
      });
    } catch (err) {
      clearWatchdog();
      log.error(COMPONENT, 'Error rendering Turnstile', { err: String(err) });
      setError('Could not display the human-verification widget.');
      cbRef.current.onUnavailable?.();
    }

    return () => {
      clearWatchdog();
      if (widgetIdRef.current) {
        try {
          turnstile.remove(widgetIdRef.current);
        } catch (e) {
          log.warn(COMPONENT, 'Error removing Turnstile widget', { err: String(e) });
        }
        widgetIdRef.current = null;
      }
    };
  }, [apiReady, trimmedKey, theme, size, action, cData]);

  // Reclaim the reserved height if the widget turns out to be invisible, and
  // give it straight back if it ever does paint (mode changed in Cloudflare, or
  // a managed widget escalated to an interactive challenge mid-session).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const graceTimer = setTimeout(() => {
      setCollapsed(el.getBoundingClientRect().height < 8);
    }, INVISIBLE_COLLAPSE_DELAY_MS);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        if (el.getBoundingClientRect().height >= 8) setCollapsed(false);
      });
      observer.observe(el);
    }

    return () => {
      clearTimeout(graceTimer);
      observer?.disconnect();
    };
  }, []);

  // Parent asked for a fresh challenge (previous token was spent on a failed submit)
  useEffect(() => {
    if (!resetKey || !widgetIdRef.current || !window.turnstile) return;
    try {
      window.turnstile.reset(widgetIdRef.current);
      setError(null);
    } catch (e) {
      log.warn(COMPONENT, 'Error resetting Turnstile widget', { err: String(e) });
    }
  }, [resetKey]);

  return (
    <div
      className={`turnstile-wrapper ${className}`}
      style={{
        position: 'relative',
        // CRITICAL FIX: Set explicit height to prevent layout shifts.
        // Released once the widget is measured to be invisible — see
        // INVISIBLE_COLLAPSE_DELAY_MS.
        minHeight: collapsed ? 0 : size === 'compact' ? '120px' : '65px',
        // CRITICAL FIX: Set pointer events to auto for the container
        pointerEvents: 'auto',
        // Ensure proper z-index layering
        zIndex: 10,
      }}
    >
      {/* Stays mounted even while `error` is set — see ALWAYS-MOUNTED CONTRACT */}
      <div
        ref={containerRef}
        style={{
          // CRITICAL FIX: Hard-isolate the widget container
          position: 'relative',
          width: '100%',
          // CRITICAL FIX: Ensure pointer events work correctly
          pointerEvents: 'auto',
          // Prevent any inherited transforms that might break positioning
          transform: 'none',
        }}
      />
      {error && (
        <div
          className='turnstile-error'
          style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.375rem' }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

// Declare Turnstile types for TypeScript
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
    };
  }
}

export default Turnstile;
